// Who may do what. This table is the single definition of authorisation
// for the whole system -- the API enforces it and the web app reads it to
// decide which menus to render. The web app deciding is a CONVENIENCE; the
// API deciding is the SECURITY. Never let the client be the only check.

export const ROLES = ['owner', 'doctor', 'reception'] as const
export type Role = (typeof ROLES)[number]

export const PERMISSIONS = [
  'staff:read',
  'staff:write',
  'patient:read',
  'patient:write',
  'audit:read',
  'product:read',
  'product:write',
  'prescription:read',
  'prescription:write',
  'billing:read',
  'billing:write',
  'billing:void',
  'analytics:read',
] as const
export type Permission = (typeof PERMISSIONS)[number]

const MATRIX: Record<Role, readonly Permission[]> = {
  // The owner is the proprietor: manages staff and reads the audit trail.
  // Prices are the owner's business: staff dispense, the proprietor sets
  // what things cost.
  owner: ['staff:read', 'staff:write', 'patient:read', 'patient:write',
          'audit:read', 'product:read', 'product:write',
          'prescription:read', 'prescription:write',
          'billing:read', 'billing:write', 'billing:void',
          // Analysis crosses every boundary at once -- a doctor's revenue
          // beside a patient's declared quantity -- so it belongs to the
          // proprietor alone. There is no analytics:write: the whole
          // section is read-only by construction.
          'analytics:read'],
  // Clinical and counter staff work with patients and nothing else. Neither
  // can grant themselves access or read who did what.
  // A doctor writes prescriptions and sees their OWN. That scoping is a
  // row-level rule enforced in the query, not a permission -- the
  // permission only says they may reach the section at all.
  doctor: ['patient:read', 'patient:write', 'prescription:read', 'prescription:write'],
  // Billing happens at the counter, so reception does it. A doctor does
  // not: prescribing and charging for what was prescribed are separate
  // jobs, and keeping them apart is the point.
  //
  // Reception raises and settles bills but cannot void one. Undoing an
  // invoice is the proprietor's decision: it is the operation that makes
  // money disappear from the day's takings, and the person who took the
  // payment should not also be the person who can erase it. The same
  // permission governs SEEING a cancelled bill -- a voided invoice exists
  // to be answered for, and the counter has no use for one.
  reception: ['patient:read', 'patient:write', 'billing:read', 'billing:write'],
}

export function can(role: Role, permission: Permission): boolean {
  return MATRIX[role].includes(permission)
}

export function permissionsFor(role: Role): readonly Permission[] {
  return MATRIX[role]
}
